import React, { useEffect, useState } from 'react'

import { useSnackbar } from 'contexts'
import { useCompanyMutationsQuery, useCanAccess, useColorMode } from 'hooks'
import { useCurrentUserCompany } from 'hooks/api'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { COMPANY_RULES } from 'validation/product'

import { Typography, Stack, Box, Tooltip } from '@mui/material'
import { Sun, Moon } from 'lucide-react'

import { Input, Button, ToggleButton } from '@repo/ui'
import { AvatarField, WideLogoField, SelectCountryField, SelectField, InfoTooltip, Loader, SettingsSection, QueryErrorState } from 'components'
import { permissionLabel } from 'utils/permissionLabels'
import { REPORTING_CURRENCIES, DEFAULT_REPORTING_CURRENCY } from '@repo/types'

const EMPTY_LOGO = { file: null as File | null, preview_url: '', isUploaded: true }

/**
 * Mirrors `APPEARANCE_OPTIONS` in StudioPage/components/EmailPreview — same
 * control, same glyphs, same sizes, so the two light/dark switches in the product
 * read as one thing. Labels are empty on purpose: a sun beside a moon is
 * unambiguous without reading, and `ariaLabel` carries the accessible name.
 */
const APPEARANCE_OPTIONS = [
  { label: '', ariaLabel: 'Light', value: 'light', icon: <Sun size={15} strokeWidth={1.5} /> },
  { label: '', ariaLabel: 'Dark', value: 'dark', icon: <Moon size={15} strokeWidth={1.5} /> },
]

const MARK_HINT = 'PNG, JPG, or SVG. Square, at least 512×512px. Max 10MB.'
const WORDMARK_HINT = 'PNG, JPG, or SVG. Wide / horizontal, around 800×200px. Max 10MB.'

export const CompanyGeneralPage = () => {
  const { open: notification } = useSnackbar()
  // Preview toggle: flip the whole app between light/dark so the admin can see
  // their logos on each background (replaces the forced light/dark preview panels).
  const { resolvedMode, toggle } = useColorMode()

  // Company (name + country).
  const methods = useForm({ mode: 'onChange' })
  const { handleSubmit, formState: { isDirty }, reset, control } = methods

  // Support — own form + Save.
  const supportMethods = useForm({
    mode: 'onChange',
    defaultValues: { support_email: '', support_phone: '' },
  })

  // Logo set. Each variant is controlled local state and saves on upload (no
  // separate Save/Cancel). The primary mark persists via the existing API.
  const [mark, setMark] = useState(EMPTY_LOGO)
  // VRG-18: the dark/wordmark variants now persist exactly like the primary
  // mark — each auto-saves on change through `logo_dark` / `logo_wide` /
  // `logo_wide_dark`. Rendering/using the wordmarks happens later in Studio
  // (VERA-267); only consumption is still deferred, storage is not.
  const [markDark, setMarkDark] = useState(EMPTY_LOGO)
  const [wordmark, setWordmark] = useState(EMPTY_LOGO)
  const [wordmarkDark, setWordmarkDark] = useState(EMPTY_LOGO)

  const { isHasAccess: canEditCompany } = useCanAccess({ action: 'manage_company' })

  // Use the "my company" endpoint (/companies/admin/me, ViewCompany) rather than
  // the list endpoint (/companies/admin, ViewCompanies) — a company-admin has the
  // former but not the latter, so the list 403'd and the page fell back to "No
  // company found" on a cold deep-load. /me returns the CompanyDto directly.
  const {
    data: company,
    isLoading: isLoadingCompanies,
    isError: isCompaniesError,
    error: companiesError,
    refetch: refetchCompanies,
  } = useCurrentUserCompany()

  const { update: updateCompany, isLoading: isUpdating } = useCompanyMutationsQuery({
    onSuccess: () => {
      notification({
        type: 'success',
        title: 'Company Updated',
        description: 'Your company details have been successfully updated',
      })
      refetchCompanies()
    },
    onError: (error) => {
      notification({
        type: 'error',
        title: 'Update Failed',
        description: error?.message || 'Failed to update company details',
      })
    },
  })

  const loading = isLoadingCompanies

  useEffect(() => {
    if (company) {
      reset({
        name: company.name,
        country: company.country,
        reporting_currency: company.reporting_currency || DEFAULT_REPORTING_CURRENCY,
      })
      setMark({ file: null, preview_url: company.logo, isUploaded: true })
      setMarkDark({ file: null, preview_url: company.logo_dark || '', isUploaded: true })
      setWordmark({ file: null, preview_url: company.logo_wide || '', isUploaded: true })
      setWordmarkDark({ file: null, preview_url: company.logo_wide_dark || '', isUploaded: true })
    }
  }, [company, reset])

  useEffect(() => {
    if (company) {
      supportMethods.reset({
        support_email: company.support_email || '',
        support_phone: company.support_phone || '',
      })
    }
  }, [company, supportMethods.reset])

  // Save name + country (logo preserved).
  const onSubmit = (formData: any) => {
    if (!company) return
    updateCompany({
      id: company.id,
      name: formData.name || company.name,
      logo: { file: null, preview_url: company.logo, isUploaded: true },
      country: formData.country || company.country,
      provider: company.provider,
      reporting_currency: formData.reporting_currency || company.reporting_currency || DEFAULT_REPORTING_CURRENCY,
    })
  }

  // Primary mark saves immediately on upload.
  const onMarkChange = (value: typeof EMPTY_LOGO) => {
    setMark(value)
    if (!company || !value.file) return
    updateCompany({
      id: company.id,
      name: company.name,
      logo: { file: value.file, preview_url: value.preview_url, isUploaded: false },
      country: company.country,
      provider: company.provider,
    })
  }

  // VRG-18: each optional variant saves immediately too — on upload AND on
  // Remove. `logo` is passed as already-uploaded so this request never touches
  // the primary mark, and the other two variants are omitted entirely so the
  // API leaves them alone (see the update use-case's `typeof !== 'undefined'`
  // guards). WideLogoField/AvatarField signal Remove with no file and an empty
  // preview_url, which reaches the API as an empty string and clears the column.
  const onVariantChange =
    (
      key: 'logo_dark' | 'logo_wide' | 'logo_wide_dark',
      setLocal: React.Dispatch<React.SetStateAction<typeof EMPTY_LOGO>>,
      previous: typeof EMPTY_LOGO,
    ) =>
    (value: typeof EMPTY_LOGO) => {
      setLocal(value)
      if (!company) return
      const isRemoval = !value.file && !value.preview_url
      // A no-op change (neither a new file nor a removal of something that was
      // there) shouldn't fire a request.
      if (!value.file && !(isRemoval && previous.preview_url)) return
      updateCompany({
        id: company.id,
        name: company.name,
        logo: { file: null, preview_url: company.logo, isUploaded: true },
        country: company.country,
        provider: company.provider,
        [key]: { file: value.file, preview_url: value.preview_url, isUploaded: false },
      })
    }

  const onSupportSubmit = (formData: any) => {
    if (!company) return
    updateCompany({
      id: company.id,
      name: company.name,
      logo: { file: null, preview_url: company.logo, isUploaded: true },
      country: company.country,
      provider: company.provider,
      support_email: formData.support_email || undefined,
      support_phone: formData.support_phone || undefined,
    })
  }

  if (loading) return <Loader />

  if (isCompaniesError) {
    // VRG-677: the shared failed-query state, not page-local red text. It names the
    // missing permission, and inside a brand's space it explains that the brand's
    // Vera-support grant is the cause and where their admin changes it.
    return (
      <QueryErrorState
        error={companiesError}
        subject="company settings"
        permissionLabel={permissionLabel('view_company')}
        onRetry={() => refetchCompanies()}
      />
    )
  }

  if (!company) {
    return (
      <Stack alignItems="center" justifyContent="center" minHeight="400px">
        <Typography variant="body1" color="text.secondary">
          No company found. Please contact support.
        </Typography>
      </Stack>
    )
  }

  const requiredLabel = (
    <Typography variant="body3" sx={{ mb: 1.5, display: 'block', color: (t) => t.palette.brand?.[600] || t.palette.primary.main }}>
      Required
    </Typography>
  )
  const optionalLabel = (
    <Typography variant="body3" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
      Optional
    </Typography>
  )

  return (
    /* spacing 8 (32px) to match ProfilePage — section gaps now carry the
       separation that heading weight used to attempt. */
    <Stack spacing={8}>
      {/* Company — name + country */}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <SettingsSection title="Company">
              <Stack pb={2} direction="row" spacing={3}>
                <Stack sx={{ flex: 1 }}>
                  <Controller
                    name="name"
                    control={control}
                    defaultValue={company.name}
                    rules={COMPANY_RULES.name}
                    render={({ field, fieldState }) => (
                      <Stack spacing={2}>
                        <Typography variant="body3">Name</Typography>
                        <Input
                          {...field}
                          value={field.value}
                          onValueChange={(value) => field.onChange(value)}
                          error={!!fieldState.error}
                          placeholder="Enter company name"
                          disabled={!canEditCompany}
                        />
                        {fieldState.error && (
                          <Typography variant="body4" color="error">
                            {fieldState.error.message}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  />
                </Stack>
                <SelectCountryField
                  label="Country"
                  name="country"
                  control={control}
                  defaultValue={company.country}
                  disabled={!canEditCompany}
                />
                <Stack sx={{ flex: 1 }}>
                  <Controller
                    name="reporting_currency"
                    control={control}
                    defaultValue={company.reporting_currency || DEFAULT_REPORTING_CURRENCY}
                    render={({ field }) => (
                      <Stack spacing={2}>
                        <Stack direction="row" alignItems="center">
                          <Typography variant="body3">Reporting currency</Typography>
                          {/* §67 — this setting takes effect on the Analytics page, not
                              here: money figures there include only rows in this
                              currency. Say where it applies and what happens to the
                              rest, since the editor can't see that surface from here. */}
                          <InfoTooltip
                            text="Analytics money figures are reported in this currency. Resales priced in another currency are left out of those figures and counted separately. Nothing is converted."
                            doc="analytics/analytics"
                          />
                        </Stack>
                        <SelectField
                          options={REPORTING_CURRENCIES.map((c) => ({
                            label: `${c.code} — ${c.label}`,
                            value: c.code,
                          }))}
                          value={field.value}
                          onChange={(value) => field.onChange(value)}
                          disabled={!canEditCompany}
                          search
                        />
                      </Stack>
                    )}
                  />
                </Stack>
              </Stack>
            <Stack direction="row" justifyContent="flex-end" spacing={2} mt={4}>
              {/* Not gated on `!isDirty` (working-conventions §54). Save reflects
                  whether work exists; Cancel must not go dead just because none does —
                  a card whose every control is inert on arrival reads as broken.
                  `!canEditCompany` and `isUpdating` stay: the first is a permission
                  block, the second is in-flight state where cancelling would race the
                  mutation (§46). */}
              <Button
                variant="secondary"
                onClick={() => reset()}
                disabled={!canEditCompany || isUpdating}
              >
                Cancel
              </Button>
              {/* §27: name the specific missing permission (not a vague "you don't have
                  permission") so the copy is actionable. */}
              <Tooltip title={!canEditCompany ? "You need the 'Manage company' permission. Ask an admin to grant it." : ''}>
                <span>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={!canEditCompany || !isDirty || isUpdating}
                  >
                    {isUpdating ? 'Saving...' : 'Save'}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </SettingsSection>
        </form>
      </FormProvider>

      {/* Logos — mark + wordmark (columns), on-light + on-dark (rows). Saves on upload. */}
      <SettingsSection
        title="Logos"
        description="Upload your brand marks. The right variant is used automatically by the background it renders on. Only the primary mark is required."
        // The same segmented control Studio uses for its email preview
        // (StudioPage/components/EmailPreview), rather than a one-off text button.
        //
        // It also resolves a real ambiguity, not just a style mismatch: the old
        // button labelled the ACTION, so while in dark mode it read "Light mode" —
        // the word on screen was always the mode you were NOT in. A segmented pair
        // shows STATE instead, and a sun beside a moon needs no reading at all,
        // which is the reasoning already written down for the Studio one.
        action={
            <Tooltip title="Preview your logos against a light or dark background">
              <span>
                <ToggleButton
                  tabs={APPEARANCE_OPTIONS}
                  tab={resolvedMode === 'dark' ? 'dark' : 'light'}
                  // useColorMode exposes a toggle, not a setter, so clicking the
                  // already-active side must be a no-op rather than a flip.
                  onChange={(v) => {
                    if (v !== resolvedMode) toggle()
                  }}
                  sx={{ flexShrink: 0 }}
                />
              </span>
            </Tooltip>
        }
        spacing={8}
      >
          {/* The Divider that used to sit here separated the heading + description
              from the content. Both now live outside the card, so the card's top
              edge does that job and the rule would be a second line saying the
              same thing.

              No wrapper Stack either — SettingsSection already spaces its children,
              so the old `<Stack spacing={8}>` is now just `spacing={8}` above. */}
          {/* Format columns */}
          <Stack direction="row" spacing={3}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Mark{' '}
                <Typography component="span" variant="body3" color="text.secondary">
                  · square
                </Typography>
              </Typography>
              <Typography variant="body3" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {MARK_HINT}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Wordmark{' '}
                <Typography component="span" variant="body3" color="text.secondary">
                  · wide
                </Typography>
              </Typography>
              <Typography variant="body3" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {WORDMARK_HINT}
              </Typography>
            </Box>
          </Stack>

          {/* On light background */}
          <Stack spacing={1.5}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              On light background
            </Typography>
            <Stack direction="row" spacing={3}>
              <Box sx={{ flex: 1 }}>
                {requiredLabel}
                <AvatarField
                  name="logo"
                  isCompany
                  value={mark}
                  onChange={onMarkChange}
                  disabled={!canEditCompany}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                {optionalLabel}
                <WideLogoField
                  value={wordmark}
                  onChange={onVariantChange('logo_wide', setWordmark, wordmark)}
                  disabled={!canEditCompany}
                />
              </Box>
            </Stack>
          </Stack>

          {/* On dark background */}
          <Stack spacing={1.5}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              On dark background
            </Typography>
            <Stack direction="row" spacing={3}>
              <Box sx={{ flex: 1 }}>
                {optionalLabel}
                <AvatarField
                  name="logo_dark"
                  isCompany
                  dark
                  value={markDark}
                  onChange={onVariantChange('logo_dark', setMarkDark, markDark)}
                  disabled={!canEditCompany}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                {optionalLabel}
                <WideLogoField
                  dark
                  value={wordmarkDark}
                  onChange={onVariantChange('logo_wide_dark', setWordmarkDark, wordmarkDark)}
                  disabled={!canEditCompany}
                />
              </Box>
            </Stack>
          </Stack>
      </SettingsSection>

      {/* Support */}
      <SettingsSection
        title="Support"
        description="Provide your brand's support contact details to help certificate owners reach your support team for assistance."
      >
        <form onSubmit={supportMethods.handleSubmit(onSupportSubmit)}>
          <Stack spacing={4}>
            <Stack pb={2} direction="row" spacing={3}>
              <Stack sx={{ flex: 1 }}>
                <Controller
                  name="support_email"
                  control={supportMethods.control}
                  rules={{
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email address',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <Stack spacing={2}>
                      <Typography variant="body3">Support Email</Typography>
                      <Input
                        {...field}
                        value={field.value}
                        onValueChange={(value) => field.onChange(value)}
                        error={!!fieldState.error}
                        disabled={!canEditCompany}
                        placeholder="support@yourbrand.com"
                      />
                      {fieldState.error && (
                        <Typography variant="body4" color="error">
                          {fieldState.error.message}
                        </Typography>
                      )}
                    </Stack>
                  )}
                />
              </Stack>
              <Stack sx={{ flex: 1 }}>
                <Controller
                  name="support_phone"
                  control={supportMethods.control}
                  rules={{
                    pattern: {
                      value: /^\+[1-9]\d{1,14}$/,
                      message: 'Please enter a valid E.164 phone number (e.g., +14155551234)',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <Stack spacing={2}>
                      <Typography variant="body3">Support Phone</Typography>
                      <Input
                        {...field}
                        value={field.value}
                        onValueChange={(value) => field.onChange(value)}
                        error={!!fieldState.error}
                        disabled={!canEditCompany}
                        placeholder="+14155551234"
                      />
                      {fieldState.error && (
                        <Typography variant="body4" color="error">
                          {fieldState.error.message}
                        </Typography>
                      )}
                    </Stack>
                  )}
                />
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={2} mt={4}>
            {/* Same as the card above (§54): the dirty check belongs on Save, not on
                the way out of the form. Permission and in-flight guards stay. */}
            <Button
              variant="secondary"
              onClick={() => supportMethods.reset()}
              disabled={isUpdating || !canEditCompany}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={!supportMethods.formState.isDirty || !supportMethods.formState.isValid || isUpdating || !canEditCompany}
            >
              {isUpdating ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </form>
      </SettingsSection>
    </Stack>
  )
}
